import { useEffect, useState } from 'react';
import { ActivityIcon, Clock, User } from '@phosphor-icons/react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { useSession } from '@clerk/clerk-react';

interface PhaseHistoryEntry {
  id: number;
  fromPhase: string | null;
  toPhase: string;
  changedAt: string;
  changedBy: string | null;
}

interface Props {
  ticketId: number;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  const timeStr = date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 0) return `Vandaag om ${timeStr}`;
  if (diffDays === 1) return `Gisteren om ${timeStr}`;
  if (diffDays < 7) return `${diffDays} dagen geleden`;
  return date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export function RenoPhaseHistoryTimeline({ ticketId }: Props) {
  const { session } = useSession();
  const [history, setHistory] = useState<PhaseHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!session || !ticketId) return;
    setIsLoading(true);
    fetchWithAuth(`${API_URL}/reno-helpdesk/tickets/${ticketId}/phase-history`, { method: 'GET' }, session)
      .then((r) => r.json())
      .then(setHistory)
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [ticketId, session]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <span className="text-sm text-[var(--muted-foreground)]">Fasegeschiedenis laden...</span>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="py-8 text-center">
        <ActivityIcon size={32} className="mx-auto mb-2 opacity-40 text-[var(--muted-foreground)]" weight="duotone" />
        <p className="text-sm text-[var(--muted-foreground)]">Nog geen fasewijzigingen</p>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {history.map((entry, index) => {
        const isFirst = index === 0;
        const isLast = index === history.length - 1;
        return (
          <div key={entry.id} className="relative flex gap-3 pb-6">
            {!isLast && (
              <div className="absolute top-8 bottom-0 left-4 w-0.5 bg-gradient-to-b from-gray-200 to-transparent" />
            )}
            <div className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 ${isFirst ? 'bg-purple-100 border-purple-300' : 'border-gray-200 bg-white'}`}>
              <ActivityIcon weight="duotone" size={16} className={isFirst ? 'text-purple-600' : 'text-gray-400'} />
            </div>
            <div className="flex-1 pt-0.5">
              <div className="mb-1">
                {entry.fromPhase ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">{entry.fromPhase}</span>
                    <span className="text-xs text-[var(--muted-foreground)]">→</span>
                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">{entry.toPhase}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[var(--muted-foreground)]">Initiële fase:</span>
                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">{entry.toPhase}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-[var(--muted-foreground)]">
                <div className="flex items-center gap-1">
                  <Clock size={12} />
                  <span>{formatDate(entry.changedAt)}</span>
                </div>
                {entry.changedBy && (
                  <div className="flex items-center gap-1">
                    <User size={12} />
                    <span>{entry.changedBy}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
